import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronDown, ChevronRight, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const PERIODS = [["year", "This Year"], ["quarter", "This Quarter"], ["month", "This Month"], ["last_week", "Last Week"], ["this_week", "This Week"], ["all", "All Time"]];
const fmt = (d) => (d ? new Date(d.length === 10 ? `${d}T12:00:00` : d).toLocaleDateString() : "—");

function MetricRow({ m }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setOpen((o) => !o)} data-testid={`rec-row-${m.key}`}>
        <td className="px-3 py-2.5 font-medium text-slate-800 flex items-center gap-1.5">{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}{m.label}</td>
        <td className="px-3 py-2.5 text-right tabular-nums text-slate-500" data-testid={`rec-current-${m.key}`}>{m.current ?? "not shown"}</td>
        <td className="px-3 py-2.5 text-xs text-slate-600">{Object.entries(m.sources).map(([s, n]) => `${s} ${n}`).join(" · ") || "No records found in any source"}</td>
        <td className="px-3 py-2.5 text-right tabular-nums">{m.duplicates_removed}</td>
        <td className="px-3 py-2.5 text-right tabular-nums">{m.historical_added}</td>
        <td className="px-3 py-2.5 text-right tabular-nums">{m.unattributed}</td>
        <td className="px-3 py-2.5 text-right tabular-nums font-bold text-slate-900" data-testid={`rec-final-${m.key}`}>{m.final}</td>
      </tr>
      {open && (
        <tr><td colSpan={7} className="bg-slate-50 px-4 py-3">
          <p className="text-xs text-slate-500 mb-2">By recruiter: {Object.entries(m.by_recruiter).map(([r, n]) => `${r} ${n}`).join(" · ") || "—"}</p>
          {m.records.length === 0 ? <p className="text-sm text-slate-400">Searched every source — none in this period.</p> : (
            <table className="w-full text-xs bg-white rounded border border-slate-200" data-testid={`rec-records-${m.key}`}>
              <thead className="text-slate-500"><tr>{["Person", "Date", "Recruiter", "Source", "Group", "Evidence"].map((h) => <th key={h} className="text-left px-2 py-1.5">{h}</th>)}</tr></thead>
              <tbody className="divide-y">{m.records.map((r, i) => (
                <tr key={`${r.pid}-${i}`}>
                  <td className="px-2 py-1.5"><Link to={`/people/detail/${r.pid}`} target="_blank" className="text-brand hover:underline font-medium">{r.name}</Link></td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{fmt(r.date)}</td>
                  <td className="px-2 py-1.5" title={r.attribution}>{r.recruiter}</td>
                  <td className="px-2 py-1.5">{r.sources.join(" + ")}</td>
                  <td className="px-2 py-1.5">{r.group}</td>
                  <td className="px-2 py-1.5 text-slate-500">{r.evidence}</td>
                </tr>))}</tbody>
            </table>)}
        </td></tr>
      )}
    </>
  );
}

// READ-ONLY reconciliation: current Recruiter Activity counts vs. complete deduplicated counts. Changes nothing.
export default function RecruiterActivityReconciliation() {
  const [period, setPeriod] = useState("year");
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get("/reports/recruiter-activity/reconciliation", { params: { period } }).then((r) => setData(r.data))
      .catch((e) => { toast.error(apiError(e)); setData({ metrics: [], review: [] }); });
  }, [period]);
  return (
    <div className="space-y-5" data-testid="ra-reconciliation">
      <PageHeader title="Recruiter Activity — Reconciliation" description="Read-only comparison of today's dashboard counts with complete, deduplicated counts from every activity source. Nothing is changed."
        actions={<Select value={period} onValueChange={setPeriod}><SelectTrigger className="w-40 bg-white" data-testid="rec-period"><SelectValue /></SelectTrigger>
          <SelectContent>{PERIODS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>} />
      {data === null ? <div className="py-16 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div> : (
        <>
          <p className="text-xs text-slate-500" data-testid="rec-scope">{fmt(data.start)} – {fmt(data.end)} · Scope: {Array.isArray(data.scope) ? data.scope.join(", ") : data.scope} · Click a row to see the people behind each count</p>
          <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm" data-testid="rec-table">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide"><tr>
                <th className="text-left px-3 py-2.5">Metric</th><th className="text-right px-3 py-2.5">Current dashboard</th><th className="text-left px-3 py-2.5">Records found by source</th>
                <th className="text-right px-3 py-2.5">Duplicates removed</th><th className="text-right px-3 py-2.5">Historical added</th><th className="text-right px-3 py-2.5">Unattributed</th><th className="text-right px-3 py-2.5">Corrected</th>
              </tr></thead>
              <tbody>{data.metrics.map((m) => <MetricRow key={m.key} m={m} />)}</tbody>
            </table>
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4" data-testid="rec-review">
            <p className="font-semibold text-amber-900 flex items-center gap-1.5 mb-2"><AlertTriangle className="w-4 h-4" />Activity needs review ({data.review.length})</p>
            {data.review.length === 0 ? <p className="text-sm text-amber-800">No data-quality issues in this period.</p> : (
              <ul className="text-xs text-amber-900 space-y-1 max-h-96 overflow-auto">{data.review.map((r, i) => (
                <li key={i}><b>{r.concept}</b> · {r.name || "Unknown person"} · {fmt(r.date)} — {r.issue}{r.evidence ? ` (${r.evidence})` : ""}</li>))}</ul>)}
          </div>
        </>
      )}
    </div>
  );
}
